import {
  getApiErrorUserMessage,
  readApiError,
  type ApiErrorResult,
} from "_shared/_errors/api-error";

export type FeeScheduleTemplateField = "name" | "category" | "description" | "fee" | "currency" | "unit" | "team";

const fieldAliases: Readonly<Record<string, FeeScheduleTemplateField>> = {
  name: "name",
  category: "category",
  categoryname: "category",
  description: "description",
  fee: "fee",
  feerate: "fee",
  currency: "currency",
  feecurrency: "currency",
  unit: "unit",
  feeunit: "unit",
  team: "team",
  teammember: "team",
};

const knownMessages: Readonly<Record<string, string>> = {
  fee_schedule_template_duplicate: "That template name is already in use. Choose a different name and try again.",
  duplicate_fee_schedule_template_name: "That template name is already in use. Choose a different name and try again.",
  fee_schedule_category_duplicate: "That category name is already in use. Choose a different name and try again.",
  duplicate_fee_schedule_category_name: "That category name is already in use. Choose a different name and try again.",
  fee_schedule_template_unavailable: "This fee template is no longer available. Refresh the template and try again.",
  fee_schedule_category_unavailable: "This fee category is no longer available. Refresh the category list and choose another category.",
  fee_schedule_category_changed: "This fee category changed after you opened it. Refresh the category list and review your changes.",
  fee_schedule_template_archived: "This fee template is archived and cannot be changed. Refresh the template list and choose an active template.",
  fee_schedule_template_archive_state: "This fee template is already in the requested archive state. Refresh the template list.",
  fee_schedule_template_changed: "This fee template changed after you opened it. Refresh the template and review your changes before you save again.",
};

const knownLegacyText = {
  "A template with that name already exists.": {
    code: "duplicate_fee_schedule_template_name",
    title: "Template name already exists",
    detail: "A template with that name already exists.",
  },
  "That fee schedule template name is already in use.": {
    code: "duplicate_fee_schedule_template_name",
    title: "Template name already exists",
    detail: "That fee schedule template name is already in use.",
  },
  "Category name is already used.": {
    code: "duplicate_fee_schedule_category_name",
    title: "Category name already exists",
    detail: "Category name is already used.",
  },
} as const;

export type FeeScheduleTemplateFailure = {
  apiError: ApiErrorResult;
  fieldErrors: Readonly<Record<FeeScheduleTemplateField, string>>;
  message?: string;
};

export function readFeeScheduleTemplateFailure(error: unknown): FeeScheduleTemplateFailure {
  const apiError = readApiError(error, { knownLegacyText });
  const fieldErrors: Partial<Record<FeeScheduleTemplateField, string>> = {};

  if (apiError.trusted) {
    for (const [wireField, messages] of Object.entries(apiError.fieldErrors)) {
      const field = fieldAliases[normalizeField(wireField)];
      const message = messages.find((candidate) => candidate.trim().length > 0);
      if (field && message && !fieldErrors[field]) fieldErrors[field] = message;
    }
  }

  const message = apiError.source === "legacy"
    ? getApiErrorUserMessage(apiError)
    : apiError.code && knownMessages[apiError.code]
    ? knownMessages[apiError.code]
    : getApiErrorUserMessage(apiError);

  return { apiError, fieldErrors: fieldErrors as Readonly<Record<FeeScheduleTemplateField, string>>, message };
}

function normalizeField(field: string): string {
  return field.replace(/[._\-\s]/g, "").toLowerCase();
}
